import { Link, NavLink, useNavigate } from "react-router-dom";
import { Shield, ChevronRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/security", label: "Security" },
  { to: "/contact", label: "Contact" },
];

export function SiteHeader({ user }) {
  const navigate = useNavigate();
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-40" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="site-logo">
          <div className="w-9 h-9 relative overflow-hidden border border-slate-900">
            <div className="absolute inset-0 bg-[#0A192F]" />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-[#B91C1C]" />
            <Shield className="absolute inset-0 m-auto w-4 h-4 text-white" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="font-serif font-bold text-[#0A192F] text-[15px] tracking-tight">Integrity First</div>
            <div className="text-caption text-[10px]">Nurse Advocates LLC</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-widest font-medium link-underline ${
                  isActive ? "text-[#B91C1C]" : "text-slate-700 hover:text-[#0A192F]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/case-intake" className="hidden sm:inline-flex text-[12px] uppercase tracking-widest border border-[#0A192F] px-4 py-2 hover:bg-[#0A192F] hover:text-white transition-colors" data-testid="cta-referral-nav">
            Refer a Case
          </Link>
          <button
            onClick={() => navigate(user ? "/portal" : "/login")}
            data-testid="btn-portal-login"
            className="btn-navy inline-flex items-center gap-1.5 text-[12px] uppercase tracking-widest px-4 py-2"
          >
            {user ? "Open Portal" : "Portal Login"} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="bg-[#0A192F] text-slate-300 mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 relative overflow-hidden border border-white">
              <div className="absolute inset-0 bg-[#0A192F]" />
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-[#B91C1C]" />
              <Shield className="absolute inset-0 m-auto w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div className="font-serif text-white font-bold">Integrity First Nurse Advocates LLC</div>
          </div>
          <p className="text-[13px] leading-relaxed max-w-md">
            Registered Nurse Legal Nurse Consulting for attorneys and VA-accredited professionals — precise medical record analysis, chronology, and merit review.
          </p>
          <div className="mt-6 flex gap-2 flex-wrap">
            <span className="badge-federal !border-white/40 !text-white/80 !bg-transparent">HIPAA-Aligned</span>
            <span className="badge-federal !border-white/40 !text-white/80 !bg-transparent">Encrypted</span>
            <span className="badge-federal !border-white/40 !text-white/80 !bg-transparent">RN Reviewed</span>
          </div>
        </div>
        <div>
          <div className="text-caption text-white/60 mb-4">Site</div>
          <ul className="space-y-2 text-[13px]">
            {NAV.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
            <li><Link to="/case-intake" className="hover:text-white">Refer a Case</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-caption text-white/60 mb-4">Disclaimers</div>
          <p className="text-[11.5px] leading-relaxed text-slate-400">
            Integrity First Nurse Advocates LLC is an independent RN legal nurse consulting firm and is not affiliated with or endorsed by the U.S. Department of Veterans Affairs or Department of Defense. Not legal advice; consult a licensed attorney. Security and compliance items are design targets pending validation against contract requirements, data types, hosting authorization, and legal review.
          </p>
        </div>
      </div>
      <div className="border-t border-white/10 py-5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between gap-3 text-[11.5px] text-slate-400">
          <div>© {new Date().getFullYear()} Integrity First Nurse Advocates LLC — All rights reserved.</div>
          <div className="font-mono uppercase tracking-widest">RN Legal Nurse Consulting</div>
        </div>
      </div>
    </footer>
  );
}
